import { useMemo } from 'react';

import { STEP_CATALOGUE_GC_PAGE_SIZE } from 'constants/appConstants';
import {
  BIOSTIMULANT_COLLECTION,
  REGULAR_COLLECTIONS,
} from 'constants/gcConstants';
import {
  G_BIOSTIMULANT_QUERY_KEY,
  M_BIOSTIMULANT_QUERY_KEY,
  M_SEARCH_BIOSTIMULANT_QUERY_KEY,
} from 'constants/queryKeys';
import { useCatalogueDropdown } from 'hooks/useCatalogueDropdown';
import { useMOrgId } from 'hooks/useMOrgId';
import { GBiostimulant } from 'interfaces/IGlobalCatalogue';
import { MBiostimulant } from 'interfaces/IMasterDb';
import { composeList } from 'utils/catalogueMergeUtils';

import { StepSelectorOption } from './stepConfig';

const toStepSelectorOption = (item: GBiostimulant): StepSelectorOption => ({
  label: item.name,
  item: item.name,
  globalId: item.globalId,
  rate: item.rate ?? null,
  cost: item.price ?? null,
  unit: item.rateUnit ?? null,
  productGroups: item.productGroups,
  actionModeId: item.actionModeId ?? null,
  snapActionMode: item.snapActionMode ?? null,
  manufacturerId: item.manufacturerId ?? null,
  snapManufacturer: item.snapManufacturer ?? null,
  supplierId: item.supplierId ?? null,
  snapSupplier: item.snapSupplier ?? null,
});

export const useBiostimulantCatalogue = (gOrgId: string | null) => {
  const mOrgId = useMOrgId();

  const {
    items,
    isLoading,
    isSearchFetching,
    isPaginating: isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    hasFetchNextPageError,
    searchTerm,
    onSearch,
    clearSearch,
  } = useCatalogueDropdown<GBiostimulant, MBiostimulant, GBiostimulant>({
    gOrgId,
    mOrgId,
    gCollectionKey: BIOSTIMULANT_COLLECTION,
    mCollectionKey: BIOSTIMULANT_COLLECTION,
    gSearchCollectionName: REGULAR_COLLECTIONS.biostimulant,
    gQueryKey: [G_BIOSTIMULANT_QUERY_KEY],
    mQueryKey: [M_BIOSTIMULANT_QUERY_KEY],
    searchQueryKey: [M_SEARCH_BIOSTIMULANT_QUERY_KEY],
    mergeItems: composeList<GBiostimulant, MBiostimulant>,
    gPageSize: STEP_CATALOGUE_GC_PAGE_SIZE,
  });

  const biostimulantOptions = useMemo(
    () => items.map(toStepSelectorOption),
    [items]
  );

  return {
    biostimulantOptions,
    isLoading,
    isSearchFetching,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    hasFetchNextPageError,
    searchTerm,
    onSearch,
    clearSearch,
  };
};
